/**
 * Ghost inclusions: venue answers to inclusion questions (e.g. whether food and beverage
 * are part of the fixed price) that the pricing schema has no field for. They are stored as
 * sentinel entries inside `IPrice.included` and must never reach couples, so every render
 * site of that array strips them first.
 *
 * Format is a whole-entry token, `[bb1:<facet>=<value>]`, holding stable ids only - labels
 * live in translations. Absence of a token means the question is unanswered, which stays
 * distinct from an explicit "not included" answer.
 */
const GHOST_INCLUSION = /^\[bb1:([a-z][a-zA-Z0-9]*)=([a-zA-Z0-9_-]+)\]$/;

/** Facet id -> value id, e.g. `{ foodBeverage: 'foodOnly' }`. */
export type GhostInclusionState = Record<string, string>;

export const isGhostInclusion = (entry: string): boolean => GHOST_INCLUSION.test(entry.trim());

/** Human-visible inclusions only. Use wherever `included` is rendered. */
export const stripGhostInclusions = (included: string[] = []): string[] =>
  included.filter((entry) => !isGhostInclusion(entry));

/** Reads every token, including facets this market does not render, so none are lost. */
export const parseGhostInclusions = (included: string[] = []): GhostInclusionState =>
  included.reduce<GhostInclusionState>((state, entry) => {
    const match = GHOST_INCLUSION.exec(entry.trim());
    if (match) state[match[1]] = match[2];
    return state;
  }, {});

/**
 * Rebuilds the stored array from the visible rows plus one token per answered facet.
 * Tokens are sorted so an unchanged set of answers always produces an identical array,
 * which keeps the inclusions verification signature stable.
 */
export const withGhostInclusions = (visible: string[], state: GhostInclusionState): string[] => [
  ...visible,
  ...Object.entries(state)
    .filter(([, value]) => Boolean(value))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([facet, value]) => `[bb1:${facet}=${value}]`),
];

/** Drops one facet's token, e.g. so an area signs only the questions it renders. */
export const omitGhostFacet = (included: string[] = [], facet: string): string[] =>
  included.filter((entry) => {
    const match = GHOST_INCLUSION.exec(entry.trim());
    return !match || match[1] !== facet;
  });

export const PRICING_STRUCTURE_FACET = 'pricingStructure';

export const PRICING_BASIS_FACET = 'pricingBasis';

export type PricingBasis = 'perHead' | 'fixed';

/** How the matrix prices are charged, or undefined while unanswered. */
export const getPricingBasis = (included: string[] = []): PricingBasis | undefined =>
  parseGhostInclusions(included)[PRICING_BASIS_FACET] as PricingBasis | undefined;

export type PricingStructure = 'allInclusive' | 'venueHirePlusOptions' | 'dryHire';

/**
 * The venue's stated pricing structure, or undefined while unanswered. Never derived:
 * readers fall back to package wording themselves when nothing is stored.
 */
export const getPricingStructure = (included: string[] = []): PricingStructure | undefined =>
  parseGhostInclusions(included)[PRICING_STRUCTURE_FACET] as PricingStructure | undefined;

/** The facet vocabulary extraction may emit; anything outside it is treated as noise. */
export const GHOST_FACET_VALUES: Record<string, readonly string[]> = {
  [PRICING_STRUCTURE_FACET]: ['allInclusive', 'venueHirePlusOptions', 'dryHire'],
  [PRICING_BASIS_FACET]: ['perHead', 'fixed'],
};

/**
 * Cleans an extraction's `included`: visible rows pass through, ghost tokens survive only
 * when both facet and value are in the vocabulary. Venue-stored data must never go through
 * this - unknown venue tokens (foreign-market facets) are preserved by design.
 * Mirrored inline in the brochure task until its pinned package includes this.
 */
export const sanitizeExtractedGhosts = (included: string[] = []): string[] =>
  included.filter((entry) => {
    const match = GHOST_INCLUSION.exec(entry.trim());
    if (!match) return true;
    return GHOST_FACET_VALUES[match[1]]?.includes(match[2]) ?? false;
  });
